/**
 * RoundaboutDiagrams — 8 inline SVG diagrams for the Roundabouts Hub.
 *
 * On-brand, scalable, no licensing concerns. Stylised top-down view with
 * subtle road texture, painted lane arrows, and the give-way signs picked
 * out so learners can identify the type at a glance.
 *
 * Usage:
 *   <RoundaboutDiagram id="standard" className="w-full aspect-square" />
 */

const STROKE = "#94a3b8"; // slate-400
const ROAD = "#334155";   // slate-700
const PAINT = "#fde68a";  // amber-200 painted markings
const ACCENT = "#10b981"; // emerald-500 — feature accent

function ArrowMark({ x, y, rotate = 0, color = PAINT, scale = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`}>
      <path d="M0 -6 L4 4 L0 1 L-4 4 Z" fill={color} />
    </g>
  );
}

function DiagramFrame({ children, label }) {
  return (
    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="200" height="200" fill="#0f172a" rx="12" />
      {/* faint grid texture */}
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#1e293b" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="200" height="200" fill="url(#grid)" rx="12" />
      {children}
      <text x="100" y="190" textAnchor="middle" fill="#cbd5e1" fontSize="9" fontWeight="700" letterSpacing="0.1em">
        {label.toUpperCase()}
      </text>
    </svg>
  );
}

// ═══════ MINI ROUNDABOUT ═══════
function MiniDiagram() {
  return (
    <DiagramFrame label="Mini">
      {/* roads */}
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="0" width="40" height="200" fill={ROAD} />
      {/* mini circle */}
      <circle cx="100" cy="100" r="14" fill="white" stroke={STROKE} strokeWidth="1.5" />
      <circle cx="100" cy="100" r="8" fill="#e2e8f0" />
      {/* approach arrows */}
      <ArrowMark x="100" y="160" rotate={0} />
      <ArrowMark x="100" y="40"  rotate={180} />
      <ArrowMark x="160" y="100" rotate={-90} />
      <ArrowMark x="40"  y="100" rotate={90} />
    </DiagramFrame>
  );
}

// ═══════ STANDARD ═══════
function StandardDiagram() {
  return (
    <DiagramFrame label="Standard">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="0" width="40" height="200" fill={ROAD} />
      <circle cx="100" cy="100" r="44" fill={ROAD} stroke={STROKE} strokeWidth="1" />
      <circle cx="100" cy="100" r="22" fill="#1e293b" stroke={ACCENT} strokeWidth="2" strokeDasharray="3 3" />
      {/* circulating arrows */}
      <ArrowMark x="100" y="56" rotate={90} color={PAINT} />
      <ArrowMark x="144" y="100" rotate={180} color={PAINT} />
      <ArrowMark x="100" y="144" rotate={-90} color={PAINT} />
      <ArrowMark x="56" y="100" rotate={0} color={PAINT} />
    </DiagramFrame>
  );
}

// ═══════ MULTI-LANE ═══════
function MultiLaneDiagram() {
  return (
    <DiagramFrame label="Multi-lane">
      <rect x="0" y="74" width="200" height="52" fill={ROAD} />
      <rect x="74" y="0" width="52" height="200" fill={ROAD} />
      <line x1="0" y1="100" x2="74" y2="100" stroke={PAINT} strokeWidth="1" strokeDasharray="4 4" />
      <line x1="126" y1="100" x2="200" y2="100" stroke={PAINT} strokeWidth="1" strokeDasharray="4 4" />
      <line x1="100" y1="0" x2="100" y2="74" stroke={PAINT} strokeWidth="1" strokeDasharray="4 4" />
      <line x1="100" y1="126" x2="100" y2="200" stroke={PAINT} strokeWidth="1" strokeDasharray="4 4" />
      <circle cx="100" cy="100" r="48" fill={ROAD} stroke={STROKE} strokeWidth="1" />
      <circle cx="100" cy="100" r="34" fill={ROAD} stroke={PAINT} strokeWidth="1" strokeDasharray="3 3" />
      <circle cx="100" cy="100" r="20" fill="#1e293b" stroke={ACCENT} strokeWidth="2" />
      <ArrowMark x="100" y="50" rotate={90} />
      <ArrowMark x="100" y="62" rotate={90} />
    </DiagramFrame>
  );
}

// ═══════ SPIRAL ═══════
function SpiralDiagram() {
  return (
    <DiagramFrame label="Spiral">
      <rect x="0" y="78" width="200" height="44" fill={ROAD} />
      <rect x="78" y="0" width="44" height="200" fill={ROAD} />
      <circle cx="100" cy="100" r="46" fill={ROAD} stroke={STROKE} strokeWidth="1" />
      {/* spiral lane lines */}
      <path d="M 70 100 a 30 30 0 1 1 60 0 a 18 18 0 1 0 -36 0" fill="none" stroke={PAINT} strokeWidth="1.5" strokeDasharray="3 2" />
      <circle cx="100" cy="100" r="14" fill="#1e293b" stroke={ACCENT} strokeWidth="2" />
      <ArrowMark x="100" y="58" rotate={90} />
    </DiagramFrame>
  );
}

// ═══════ TRAFFIC LIGHT ═══════
function TrafficLightDiagram() {
  return (
    <DiagramFrame label="Traffic-light">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="0" width="40" height="200" fill={ROAD} />
      <circle cx="100" cy="100" r="44" fill={ROAD} stroke={STROKE} strokeWidth="1" />
      <circle cx="100" cy="100" r="22" fill="#1e293b" stroke={ACCENT} strokeWidth="2" />
      {/* traffic lights */}
      {[
        [100, 50], [148, 100], [100, 150], [52, 100],
      ].map(([cx, cy], i) => (
        <g key={i}>
          <rect x={cx - 4} y={cy - 8} width="8" height="16" rx="2" fill="#0f172a" stroke={STROKE} strokeWidth="0.5" />
          <circle cx={cx} cy={cy - 4} r="1.6" fill="#ef4444" />
          <circle cx={cx} cy={cy} r="1.6" fill="#fbbf24" />
          <circle cx={cx} cy={cy + 4} r="1.6" fill="#22c55e" />
        </g>
      ))}
    </DiagramFrame>
  );
}

// ═══════ DOUBLE MINI ═══════
function DoubleMiniDiagram() {
  return (
    <DiagramFrame label="Double mini">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="55" y="0" width="40" height="200" fill={ROAD} />
      <rect x="105" y="0" width="40" height="200" fill={ROAD} />
      <circle cx="75" cy="100" r="13" fill="white" stroke={STROKE} strokeWidth="1.5" />
      <circle cx="75" cy="100" r="7" fill="#e2e8f0" />
      <circle cx="125" cy="100" r="13" fill="white" stroke={STROKE} strokeWidth="1.5" />
      <circle cx="125" cy="100" r="7" fill="#e2e8f0" />
      <ArrowMark x="75" y="142" rotate={0} />
      <ArrowMark x="125" y="142" rotate={0} />
    </DiagramFrame>
  );
}

// ═══════ LARGE / HIGH-SPEED ═══════
function LargeDiagram() {
  return (
    <DiagramFrame label="Large">
      <rect x="0" y="68" width="200" height="64" fill={ROAD} />
      <rect x="68" y="0" width="64" height="200" fill={ROAD} />
      <line x1="0" y1="100" x2="68" y2="100" stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <line x1="132" y1="100" x2="200" y2="100" stroke={PAINT} strokeWidth="1" strokeDasharray="6 4" />
      <circle cx="100" cy="100" r="60" fill={ROAD} stroke={STROKE} strokeWidth="1" />
      <circle cx="100" cy="100" r="46" fill={ROAD} stroke={PAINT} strokeWidth="1" strokeDasharray="4 3" />
      <circle cx="100" cy="100" r="28" fill="#1e293b" stroke={ACCENT} strokeWidth="2" />
      <ArrowMark x="100" y="46" rotate={90} scale={1.2} />
      <ArrowMark x="154" y="100" rotate={180} scale={1.2} />
    </DiagramFrame>
  );
}

// ═══════ UNUSUAL / MAGIC ═══════
function UnusualDiagram() {
  return (
    <DiagramFrame label="Unusual">
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <rect x="80" y="0" width="40" height="200" fill={ROAD} />
      {/* central + 4 satellite mini-roundabouts */}
      <circle cx="100" cy="100" r="22" fill="#1e293b" stroke={ACCENT} strokeWidth="2" strokeDasharray="3 2" />
      {[
        [100, 50], [150, 100], [100, 150], [50, 100],
      ].map(([cx, cy], i) => (
        <g key={i}>
          <circle cx={cx} cy={cy} r="9" fill="white" stroke={STROKE} strokeWidth="1" />
          <circle cx={cx} cy={cy} r="5" fill="#e2e8f0" />
        </g>
      ))}
    </DiagramFrame>
  );
}

const DIAGRAMS = {
  mini: MiniDiagram,
  standard: StandardDiagram,
  multi_lane: MultiLaneDiagram,
  spiral: SpiralDiagram,
  traffic_light: TrafficLightDiagram,
  double_mini: DoubleMiniDiagram,
  large: LargeDiagram,
  unusual: UnusualDiagram,
};

export default function RoundaboutDiagram({ id, className = "w-full h-full" }) {
  const Cmp = DIAGRAMS[id] || StandardDiagram;
  return (
    <div className={className} data-testid={`roundabout-diagram-${id}`}>
      <Cmp />
    </div>
  );
}
